
<template>
  <div
    class="dialogs-container"
    :class="{ 'dark-theme': isDarkTheme }"
  >
    <!-- =====================================================
         HEADER
    ====================================================== -->

    <FeedHeader />

    <!-- =====================================================
         MAIN CONTENT
    ====================================================== -->

    <div class="dialogs-content">

      <!-- LEFT SIDEBAR -->

      <DialogSidebar
        :dialogs="dialogs"
        :selected-dialog-id="selectedDialogId"
        :selected-request-id="selectedRequestId"
        :selected-category="selectedCategory"
        :chat-requests="chatRequests"
        :is-loading="isLoadingDialogs"
        :total-unread-count="totalUnreadCount"
        @selectCategory="handleSelectCategory"
        @selectDialog="handleSelectDialog"
        @selectRequest="handleSelectRequest"
        @accept="handleRequestAccept"
        @decline="handleRequestDecline"
        @addNewChat="openCreateChatModal"
        @deleteDialog="askDeleteChat"
      />

      <!-- ===================================================
           SELECTED CHAT
      ==================================================== -->

      <div
        v-if="selectedDialog"
        class="chat-with-sidebar"
      >
        <ChatWindow
          :key="selectedDialog.id"
          :chat-id="selectedDialog.id"
          :current-user-id="currentUserId"
          :interlocutor="selectedDialog.interlocutor"
          :source="selectedDialog.source"
          @chatCreated="handleChatCreated"
        />

        <SettingsSidebar
          :key="`settings-${selectedDialog.id}`"
          :user-id="currentUserId"
          :interlocutor-id="selectedDialog.interlocutor.userId"
          @deleteChat="handleDeleteChat"
        />
      </div>

      <!-- ===================================================
           GHOST CHAT
      ==================================================== -->

      <div
        v-else-if="ghostUser"
        class="chat-with-sidebar"
      >
        <ChatWindow
          :key="ghostUser.userId"
          :chat-id="null"
          :current-user-id="currentUserId"
          :interlocutor="ghostUser"
          :source="dialogSource"
          @chatCreated="handleChatCreated"
        />

        <SettingsSidebar
          :key="`ghost-settings-${ghostUser.userId}`"
          :user-id="currentUserId"
          :interlocutor-id="ghostUser.userId"
          @deleteChat="handleGhostDeleteChat"
        />
      </div>

      <!-- ===================================================
           REQUEST WINDOW
      ==================================================== -->

      <div
        v-else-if="selectedRequestId"
        class="chat-with-sidebar"
      >
        <RequestWindow
          v-if="selectedRequest"
          :request="selectedRequest"
          @accept="handleRequestAccept"
          @decline="handleRequestDecline"
        />

        <div class="request-separator"></div>
      </div>

      <!-- ===================================================
           EMPTY CHAT PLACEHOLDER
      ==================================================== -->


      <div
        v-else
        class="chat-placeholder"
      >
        <div class="chat-placeholder-inner">

          <!-- ILLUSTRATION -->

          <div class="placeholder-illustration">

            <div class="illustration-glow"></div>

            <div class="illustration-ring">

              <div class="illustration-chat">

                <!-- INCOMING MESSAGE -->

                <div class="illustration-message incoming">
                  <span class="illustration-line long"></span>
                  <span class="illustration-line short"></span>
                </div>

                <!-- OUTGOING MESSAGE -->

                <div class="illustration-message outgoing">
                  <span class="illustration-line long"></span>
                  <span class="illustration-line short"></span>
                </div>

                <!-- TYPING -->

                <div class="illustration-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>

              </div>

              <!-- FLOATING CHECK -->

              <div class="illustration-check">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12L10 17L19 7"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <!-- FLOATING PLUS -->

              <div class="illustration-plus">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </div>

            </div>
          </div>

          <!-- LABEL -->

          <div class="placeholder-badge">
            <span class="placeholder-badge-dot"></span>
            ВАШИ СООБЩЕНИЯ
          </div>

          <!-- TEXT -->

          <h2 class="placeholder-title">
            Начните общение
          </h2>

          <p class="chat-placeholder-text">
            Выберите диалог слева, чтобы продолжить
            переписку или начать новый разговор
          </p>

          <!-- HINT -->

          <div class="placeholder-hint">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M19 12H5M5 12L11 6M5 12L11 18"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <span>
              Все ваши диалоги находятся слева
            </span>
          </div>

        </div>
      </div>


    </div>

    <!-- =====================================================
         FOOTER
    ====================================================== -->

    <Footer class="dialogs-footer" />

  </div>

  <!-- =======================================================
       FRIENDS DRAWER
  ======================================================== -->

  <FriendsDrawer
    v-model="isFriendsDrawerOpen"
    @select="handleOpenProfile"
  />

  <!-- =======================================================
       DELETE CHAT MODAL
  ======================================================== -->

  <Teleport to="body">
    <Transition name="delete-modal">
      <div
        v-if="confirmDeleteVisible"
        class="modal-overlay"
        :class="{ 'dark-theme': isDarkTheme }"
        @click.self="cancelDeleteChat"
      >
        <div
          class="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-chat-title"
          aria-describedby="delete-chat-description"
        >
          <!-- ICON -->

          <div class="modal-icon-container">
            <img
              src="/delete-chat.png"
              alt=""
              class="modal-icon"
            />
          </div>

          <!-- TITLE -->

          <h3
            id="delete-chat-title"
            class="modal-title"
          >
            Удалить чат?
          </h3>

          <!-- DESCRIPTION -->

          <p
            id="delete-chat-description"
            class="modal-text"
          >
            Вы действительно хотите удалить этот диалог?
            Это действие необратимо.
          </p>

          <!-- BUTTONS -->

          <div class="modal-buttons">

            <button
              type="button"
              class="cancel-btn"
              :disabled="isDeletingChat"
              @click="cancelDeleteChat"
            >
              Отмена
            </button>

            <button
              type="button"
              class="delete-btn"
              :disabled="isDeletingChat"
              @click="confirmDeleteChat"
            >
              <span
                v-if="isDeletingChat"
                class="delete-spinner"
              ></span>

              <span v-else>
                Удалить
              </span>
            </button>

          </div>

        </div>
      </div>
    </Transition>
  </Teleport>

</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount
} from "vue";

import {
  useRoute,
  useRouter
} from "vue-router";

import {
  authStateReady
} from "../api/ChatsService";

import FeedHeader from "../components/FeedHeader.vue";
import DialogSidebar from "../components/chats/DialogSidebar.vue";
import ChatWindow from "../components/chats/ChatWindow.vue";
import SettingsSidebar from "../components/chats/SettingsSidebar.vue";

import ChatsService from "../api/ChatsService.js";
import RequestsService from "../api/RequestsService.js";

import Footer from "../components/MiniFooter.vue";
import RequestWindow from "../components/chats/RequestWindow.vue";
import FriendsDrawer from "../components/FriendsDrawer.vue";

// ============================================================
// ROUTER
// ============================================================

const route = useRoute();
const router = useRouter();

// ============================================================
// STATE — DIALOGS
// ============================================================

const dialogs = ref([]);
const selectedDialogId = ref(null);
const selectedRequestId = ref(null);

const currentUserId = ref(null);

const isLoadingDialogs = ref(true);

const selectedCategory = ref("Сообщения");

const chatRequests = ref([]);

const totalUnreadCount = ref(0);

// ============================================================
// STATE — UI
// ============================================================

const isFriendsDrawerOpen = ref(false);

const confirmDeleteVisible = ref(false);
const dialogToDelete = ref(null);
const isDeletingChat = ref(false);

// ============================================================
// THEME — LOCAL
// ============================================================

const isDarkTheme = ref(false);

let dialogsThemeObserver = null;

function updateDialogsTheme() {
  isDarkTheme.value =
    document.documentElement.getAttribute("data-theme") === "dark";
}

function startObservingTheme() {
  updateDialogsTheme();

  dialogsThemeObserver = new MutationObserver(
    updateDialogsTheme
  );

  dialogsThemeObserver.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: ["data-theme"]
    }
  );
}

function stopObservingTheme() {
  dialogsThemeObserver?.disconnect();
  dialogsThemeObserver = null;
}

// ============================================================
// SOUND
// ============================================================

const messageSound = new Audio(
  "/sounds/new-message.mp3"
);

messageSound.volume = 0.35;

function playNewMessageSound() {
  messageSound.currentTime = 0;

  messageSound.play().catch(() => {
    console.warn(
      "Браузер заблокировал воспроизведение звука"
    );
  });
}

// ============================================================
// GHOST USER
// ============================================================

const ghostUser = computed(() => {
  const q = route.query;

  if (
    q.ghost_user_id &&
    q.name &&
    q.image &&
    q.specifics
  ) {
    return {
      userId: q.ghost_user_id,
      name: q.name,
      avatar: q.image,
      specifics: q.specifics
    };
  }

  return null;
});

const dialogSource = computed(() => {
  return route.query.dialogSource || null;
});

// ============================================================
// SELECTED DIALOG
// ============================================================

const selectedDialog = computed(() => {
  return (
    dialogs.value.find(
      dialog => dialog.id === selectedDialogId.value
    ) || null
  );
});

// ============================================================
// SELECTED REQUEST
// ============================================================

const selectedRequest = computed(() => {
  return (
    chatRequests.value.find(
      request => request.id === selectedRequestId.value
    ) || null
  );
});

// ============================================================
// REALTIME LISTENER
// ============================================================

let unsubscribeAll = null;

function stopListeningForAllNewMessages() {
  if (unsubscribeAll) {
    unsubscribeAll();
    unsubscribeAll = null;
  }
}

// ============================================================
// MAP CHAT
// ============================================================

function mapChatToDialog(chat) {
  return {
    id: chat.chatId,

    chatType: chat.chatType || "default",

    source:
      chat.source ||
      chat.chatSource ||
      null,

    interlocutor: {
      userId:
        chat.interlocutor?.user_id,

      name:
        chat.interlocutor?.name ||
        "Без имени",

      specifics:
        chat.interlocutor?.specifics ||
        "Специальность не указана",

      avatar:
        chat.interlocutor?.imageURL,

      isOnline:
        chat.interlocutor?.is_online ?? false
    },

    lastMessage:
      chat.lastMessage,

    lastMessageText:
      chat.previewText,

    lastMessageDate:
      chat.lastMessage?.sendDate || null
  };
}

// ============================================================
// SORT DIALOGS
// ============================================================

function sortDialogs(items) {
  return [...items].sort(
    (a, b) => {
      const dateA = new Date(
        a.lastMessageDate || 0
      ).getTime() || 0;

      const dateB = new Date(
        b.lastMessageDate || 0
      ).getTime() || 0;

      return dateB - dateA;
    }
  );
}

// ============================================================
// LOAD DIALOGS
// ============================================================

async function loadDialogs() {
  try {
    isLoadingDialogs.value = true;

    const fetchedDialogs =
      await ChatsService.getMyChats();

    dialogs.value = sortDialogs(
      (fetchedDialogs || []).map(
        mapChatToDialog
      )
    );

    if (selectedCategory.value === "Сообщения") {
      startListeningForAllNewMessages();
    }

  } catch (error) {
    console.error(
      "Ошибка загрузки диалогов:",
      error
    );

  } finally {
    isLoadingDialogs.value = false;
  }
}

// ============================================================
// LISTEN ALL NEW MESSAGES
// ============================================================

function startListeningForAllNewMessages() {
  stopListeningForAllNewMessages();

  if (
    selectedCategory.value !== "Сообщения" ||
    dialogs.value.length === 0
  ) {
    return;
  }

  const chatIds = dialogs.value
    .map(dialog => dialog.id)
    .filter(Boolean);

  if (chatIds.length === 0) {
    return;
  }

  unsubscribeAll =
    ChatsService.listenForAllNewMessages(
      chatIds,
      handleRealtimeMessage
    );
}

// ============================================================
// REALTIME MESSAGE HANDLER
// ============================================================

function handleRealtimeMessage(newMessage) {
  console.log(
    "Новое сообщение в списке чатов:",
    newMessage
  );

  const chatIndex = dialogs.value.findIndex(
    chat => chat.id === newMessage.chatId
  );

  if (chatIndex === -1) {
    return;
  }

  const dialog = dialogs.value[chatIndex];

  const isOwnMessage =
    newMessage.senderId === currentUserId.value;

  if (!isOwnMessage) {
    playNewMessageSound();
  }

  const previewText =
    newMessage.textMessage?.trim() ||
    (newMessage.imageUrl ? "📷 Фото" : "") ||
    (
      newMessage.attachmentName
        ? `📎 ${newMessage.attachmentName}`
        : ""
    ) ||
    (
      newMessage.attachmentUrl
        ? "📎 Файл"
        : ""
    );

  dialogs.value[chatIndex] = {
    ...dialog,

    lastMessageText:
      isOwnMessage
        ? `Вы: ${previewText}`
        : previewText,

    lastMessageDate:
      newMessage.sendDate ||
      new Date().toISOString(),

    lastMessageSenderId:
      newMessage.senderId,

    lastMessageIsRead:
      newMessage.isRead ?? false
  };

  dialogs.value = sortDialogs(dialogs.value);
}

// ============================================================
// CATEGORY
// ============================================================

function handleSelectCategory(value) {
  selectedCategory.value = value;
}

watch(selectedCategory, async value => {
  if (value === "Сообщения") {
    startListeningForAllNewMessages();
    return;
  }

  stopListeningForAllNewMessages();

  if (value === "Запросы") {
    await loadRequests();
  }
});

// ============================================================
// LOAD REQUESTS
// ============================================================

async function loadRequests() {
  if (!currentUserId.value) {
    return;
  }

  try {
    const fetched =
      await RequestsService.getMyRequests(
        currentUserId.value
      );

    chatRequests.value = fetched || [];

    console.log(
      "Запросы получены:",
      chatRequests.value
    );

  } catch (error) {
    console.error(
      "Ошибка загрузки запросов:",
      error
    );
  }
}

// ============================================================
// SELECT DIALOG
// ============================================================

function handleSelectDialog(dialogId) {
  console.log(
    "Выбран диалог:",
    dialogId
  );

  selectedDialogId.value = dialogId;
  selectedRequestId.value = null;
}

// ============================================================
// SELECT REQUEST
// ============================================================

function handleSelectRequest(requestId) {
  console.log(
    "Выбран запрос:",
    requestId
  );

  selectedRequestId.value = requestId;
  selectedDialogId.value = null;
}

// ============================================================
// ACCEPT REQUEST
// ============================================================

async function handleRequestAccept(id) {
  try {
    const request = chatRequests.value.find(
      item => item.id === id
    );

    if (!request) {
      return;
    }

    await RequestsService.acceptRequest(
      currentUserId.value,
      request.user.user_id,
      request.comment,
      request.id
    );

    await RequestsService.deleteRequest(id);

    chatRequests.value =
      chatRequests.value.filter(
        item => item.id !== id
      );

    selectedRequestId.value = null;

  } catch (error) {
    console.error(
      "Ошибка при принятии запроса:",
      error
    );
  }
}

// ============================================================
// DECLINE REQUEST
// ============================================================

async function handleRequestDecline(id) {
  try {
    await RequestsService.deleteRequest(id);

    chatRequests.value =
      chatRequests.value.filter(
        item => item.id !== id
      );

    selectedRequestId.value = null;

  } catch (error) {
    console.error(
      "Ошибка при отклонении запроса:",
      error
    );
  }
}

// ============================================================
// CHAT CREATED
// ============================================================

async function handleChatCreated(newChatId) {
  console.log(
    "Новый чат создан:",
    newChatId
  );

  selectedDialogId.value = newChatId;
  selectedRequestId.value = null;

  try {
    const fetchedDialogs =
      await ChatsService.getMyChats();

    dialogs.value = sortDialogs(
      (fetchedDialogs || []).map(
        mapChatToDialog
      )
    );

    startListeningForAllNewMessages();

    await router.replace({
      path: "/dialogs"
    });

  } catch (error) {
    console.error(
      "Ошибка обновления списка после создания чата:",
      error
    );
  }
}

// ============================================================
// CREATE CHAT
// ============================================================

function openCreateChatModal() {
  isFriendsDrawerOpen.value = true;
}

// ============================================================
// FRIEND SELECT
// ============================================================

function handleOpenProfile(friend) {
  // Сохраняем существующее поведение:
  // выбор профиля обрабатывается FriendsDrawer.
}

// ============================================================
// DELETE — OPEN CONFIRMATION
// ============================================================

function askDeleteChat(dialog = null) {
  if (isDeletingChat.value) {
    return;
  }

  const targetDialog =
    dialog?.id
      ? dialog
      : selectedDialog.value;

  if (!targetDialog?.id) {
    return;
  }

  dialogToDelete.value = targetDialog;

  confirmDeleteVisible.value = true;
}

// ============================================================
// DELETE — CANCEL
// ============================================================

function cancelDeleteChat() {
  if (isDeletingChat.value) {
    return;
  }

  confirmDeleteVisible.value = false;
  dialogToDelete.value = null;
}

// ============================================================
// DELETE — BACKEND
// ============================================================

async function deleteChatByDialog(targetDialog) {
  const chatId = targetDialog?.id;

  const interlocutorId =
    targetDialog?.interlocutor?.userId;

  console.group("DELETE CHAT");

  console.log(
    "targetDialog:",
    targetDialog
  );

  console.log(
    "chatId:",
    chatId
  );

  console.log(
    "interlocutorId:",
    interlocutorId
  );

  console.groupEnd();

  if (!chatId || !interlocutorId) {
    console.warn(
      "Не хватает данных для удаления:",
      {
        chatId,
        interlocutorId
      }
    );

    return false;
  }

  stopListeningForAllNewMessages();

  try {
    const result =
      await ChatsService.deleteChat(
        interlocutorId,
        chatId
      );

    console.log(
      "Результат удаления:",
      result
    );

    if (!result?.success) {
      console.error(
        "Backend не удалил чат:",
        result?.error || "Unknown error"
      );

      return false;
    }

    dialogs.value =
      dialogs.value.filter(
        chat => chat.id !== chatId
      );

    if (selectedDialogId.value === chatId) {
      selectedDialogId.value = null;
    }

    console.log(
      "Чат успешно удалён:",
      chatId
    );

    return true;

  } catch (error) {
    console.error(
      "Ошибка удаления чата:",
      error
    );

    return false;

  } finally {
    startListeningForAllNewMessages();
  }
}

// ============================================================
// DELETE — CONFIRM FROM MODAL
// ============================================================

async function confirmDeleteChat() {
  if (isDeletingChat.value) {
    return;
  }

  const targetDialog =
    dialogToDelete.value ||
    selectedDialog.value;

  if (!targetDialog) {
    return;
  }

  isDeletingChat.value = true;

  try {
    const success =
      await deleteChatByDialog(targetDialog);

    if (success) {
      confirmDeleteVisible.value = false;
      dialogToDelete.value = null;
    }

  } finally {
    isDeletingChat.value = false;
  }
}

// ============================================================
// DELETE — FROM SETTINGS SIDEBAR
// ============================================================

async function handleDeleteChat() {
  if (isDeletingChat.value) {
    return;
  }

  const dialog = selectedDialog.value;

  if (!dialog) {
    return;
  }

  isDeletingChat.value = true;

  try {
    await deleteChatByDialog(dialog);

  } finally {
    isDeletingChat.value = false;
  }
}

// ============================================================
// GHOST CHAT DELETE
// ============================================================

function handleGhostDeleteChat() {
  // Ghost-чат ещё не создан на сервере.
  // Оставляем удаление без backend-запроса.
}

// ============================================================
// ESCAPE
// ============================================================

function handleEscapeKey(event) {
  if (event.key !== "Escape") {
    return;
  }

  if (confirmDeleteVisible.value) {
    cancelDeleteChat();
    return;
  }

  if (isFriendsDrawerOpen.value) {
    isFriendsDrawerOpen.value = false;
    return;
  }

  selectedDialogId.value = null;
  selectedRequestId.value = null;

  if (route.query.ghost_user_id) {
    const nextQuery = {
      ...route.query
    };

    delete nextQuery.ghost_user_id;
    delete nextQuery.name;
    delete nextQuery.image;
    delete nextQuery.specifics;
    delete nextQuery.dialogSource;

    router.replace({
      path: route.path,
      query: nextQuery
    }).catch(error => {
      console.error(
        "Ошибка закрытия ghost-чата:",
        error
      );
    });
  }
}

// ============================================================
// LIFECYCLE — MOUNT
// ============================================================

onMounted(async () => {
  // THEME

  startObservingTheme();

  // KEYBOARD

  window.addEventListener(
    "keydown",
    handleEscapeKey
  );

  // AUTH

  try {
    const user = await authStateReady();

    if (user) {
      currentUserId.value = user.uid;
    }

    console.log(
      "CURRENT USER:",
      currentUserId.value
    );

    // DIALOGS

    await loadDialogs();

    // REQUESTS (if needed)

    if (selectedCategory.value === "Запросы") {
      await loadRequests();
    }

  } catch (error) {
    console.error(
      "Ошибка инициализации диалогов:",
      error
    );

    isLoadingDialogs.value = false;
  }
});

// ============================================================
// LIFECYCLE — UNMOUNT
// ============================================================

onBeforeUnmount(() => {
  stopListeningForAllNewMessages();

  stopObservingTheme();

  window.removeEventListener(
    "keydown",
    handleEscapeKey
  );

  messageSound.pause();
});
</script>

<style scoped>

/* ============================================================
   DIALOGS PAGE — LIGHT THEME
============================================================ */

.dialogs-container {
  --dialogs-bg: #ffffff;
  --dialogs-surface: #ffffff;

  --dialogs-text: #222b38;
  --dialogs-secondary: #8993a2;
  --dialogs-muted: #a4adba;

  --dialogs-border: rgba(31, 55, 90, 0.075);

  --dialogs-accent: #2173e5;
  --dialogs-accent-soft: rgba(33, 115, 229, 0.08);

  --dialogs-danger: #e45454;
  --dialogs-danger-hover: #d94444;

  --dialogs-cancel-bg: #f3f5f8;
  --dialogs-cancel-hover: #e9edf3;

  /* ORIGINAL DIMENSIONS — DO NOT CHANGE */

  width: 100vw;
  height: 100vh;

  position: relative;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background: var(--dialogs-bg);

  color: var(--dialogs-text);

  font-family: "Eastman-Regular", sans-serif;
}

/* ============================================================
   DARK THEME — LOCAL CLASS
============================================================ */

.dialogs-container.dark-theme {
  --dialogs-bg: #161616;
  --dialogs-surface: #161616;

  --dialogs-text: #f1f2f4;
  --dialogs-secondary: #a0a6ae;
  --dialogs-muted: #777e89;

  --dialogs-border: rgba(255, 255, 255, 0.075);

  --dialogs-accent: #83b6ff;
  --dialogs-accent-soft: rgba(111, 168, 255, 0.12);

  --dialogs-danger: #ff7979;
  --dialogs-danger-hover: #ff6060;

  --dialogs-cancel-bg: #252525;
  --dialogs-cancel-hover: #303030;
}

/* ============================================================
   MAIN CONTENT
   ORIGINAL DIMENSIONS PRESERVED
============================================================ */

.dialogs-content {
  display: flex;

  flex: 1;

  width: 1400px;
  height: calc(100vh - 180px);

  overflow: hidden;

  margin-left: calc(50% - 700px);

  position: relative;

  background: var(--dialogs-bg);
}

/* ============================================================
   CHAT WITH SIDEBAR
============================================================ */

.chat-with-sidebar {
  display: flex;

  flex: 1;

  min-width: 0;

  position: relative;

  background: var(--dialogs-bg);
}

/* ============================================================
   REQUEST SEPARATOR
============================================================ */

.request-separator {
  width: 1px;

  flex-shrink: 0;

  background: var(--dialogs-border);
}

/* ============================================================
   CHAT PLACEHOLDER
============================================================ */

.chat-placeholder {
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 0;

  position: relative;

  overflow: hidden;

  background: var(--dialogs-bg);
}

/* ============================================================
   PLACEHOLDER CONTENT
============================================================ */

.chat-placeholder-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;

  max-width: 340px;

  text-align: center;

  animation:
    placeholderAppear 0.45s ease both;
}

/* ============================================================
   PLACEHOLDER ILLUSTRATION
============================================================ */

.placeholder-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-placeholder-image {
  width: 120px;
  height: auto;

  margin-bottom: 16px;

  opacity: 0.9;

  object-fit: contain;

  filter:
    drop-shadow(
      0 8px 18px rgba(33, 115, 229, 0.055)
    );

  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.chat-placeholder-inner:hover
.chat-placeholder-image {
  transform: translateY(-3px);
}

.dialogs-container.dark-theme
.chat-placeholder-image {
  opacity: 0.78;

  filter:
    drop-shadow(
      0 8px 18px rgba(0, 0, 0, 0.14)
    );
}

/* ============================================================
   PLACEHOLDER TEXT
============================================================ */

.chat-placeholder-text {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 16px;

  line-height: 1.5;
  letter-spacing: -0.5px;

  color: var(--dialogs-secondary);

  text-align: center;

  transition: color 0.2s ease;
}

/* ============================================================
   FOOTER
============================================================ */

.dialogs-footer {
  flex-shrink: 0;

  width: 100%;
}

/* ============================================================
   DELETE MODAL — LOCAL LIGHT THEME
============================================================ */

.modal-overlay {
  --modal-surface: #ffffff;
  --modal-text-primary: #222b38;
  --modal-text-secondary: #858e9c;

  --modal-border: rgba(31, 55, 90, 0.075);

  --modal-cancel-surface: #f3f5f8;
  --modal-cancel-hover: #e9edf3;

  --modal-danger: #eb5757;
  --modal-danger-hover: #d94444;

  position: fixed;

  inset: 0;

  width: 100vw;
  height: 100vh;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 9999;

  background: rgba(12, 18, 28, 0.38);

  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

/* ============================================================
   DELETE MODAL — LOCAL DARK THEME
============================================================ */

.modal-overlay.dark-theme {
  --modal-surface: #161616;
  --modal-text-primary: #f1f2f4;
  --modal-text-secondary: #a0a6ae;

  --modal-border: rgba(255, 255, 255, 0.09);

  --modal-cancel-surface: #262626;
  --modal-cancel-hover: #303030;

  --modal-danger: #eb5757;
  --modal-danger-hover: #d94444;

  background: rgba(0, 0, 0, 0.68);
}

/* ============================================================
   MODAL
   ORIGINAL WIDTH AND PADDING
============================================================ */

.modal {
  width: 360px;

  padding: 24px;

  background: var(--modal-surface);

  border: 1px solid var(--modal-border);

  border-radius: 18px;

  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.18);

  text-align: center;
}

.modal-overlay.dark-theme .modal {
  box-shadow:
    0 24px 65px rgba(0, 0, 0, 0.42);
}

/* ============================================================
   MODAL ICON
============================================================ */

.modal-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 12px;
}

.modal-icon {
  width: 48px;
  height: 48px;

  object-fit: contain;

  filter:
    drop-shadow(
      0 5px 10px rgba(235, 87, 87, 0.09)
    );
}

/* ============================================================
   MODAL TITLE
============================================================ */

.modal-title {
  margin: 0;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;

  line-height: 1.35;
  letter-spacing: -0.3px;

  color: var(--modal-text-primary);
}

/* ============================================================
   MODAL TEXT
============================================================ */

.modal-text {
  margin: 12px 0 20px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;

  line-height: 1.5;

  color: var(--modal-text-secondary);
}

/* ============================================================
   MODAL BUTTONS
============================================================ */

.modal-buttons {
  display: flex;
  justify-content: space-between;

  gap: 12px;
}

.cancel-btn,
.delete-btn {
  flex: 1;

  padding: 10px 0;

  border: none;
  border-radius: 10px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;

  cursor: pointer;

  transition:
    background 0.18s ease,
    transform 0.18s ease,
    opacity 0.18s ease;
}

/* ============================================================
   CANCEL BUTTON
============================================================ */

.cancel-btn {
  background: var(--modal-cancel-surface);

  color: var(--modal-text-primary);
}

.cancel-btn:hover:not(:disabled) {
  background: var(--modal-cancel-hover);
}

/* ============================================================
   DELETE BUTTON
============================================================ */

.delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;

  background: var(--modal-danger);

  color: #ffffff;
}

.delete-btn:hover:not(:disabled) {
  background: var(--modal-danger-hover);
}

/* ============================================================
   BUTTON STATES
============================================================ */

.cancel-btn:disabled,
.delete-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.cancel-btn:active:not(:disabled),
.delete-btn:active:not(:disabled) {
  transform: scale(0.97);
}

.cancel-btn:focus-visible,
.delete-btn:focus-visible {
  outline: 2px solid #2173e5;
  outline-offset: 3px;
}

/* ============================================================
   DELETE SPINNER
============================================================ */

.delete-spinner {
  width: 14px;
  height: 14px;

  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;

  border-radius: 50%;

  animation:
    deleteSpinnerRotation 0.7s linear infinite;
}

@keyframes deleteSpinnerRotation {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================================
   PLACEHOLDER ANIMATION
============================================================ */

@keyframes placeholderAppear {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================================
   MODAL ANIMATION
============================================================ */

.delete-modal-enter-active,
.delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.delete-modal-enter-active .modal,
.delete-modal-leave-active .modal {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.delete-modal-enter-from,
.delete-modal-leave-to {
  opacity: 0;
}

.delete-modal-enter-from .modal {
  opacity: 0;

  transform:
    translateY(10px)
    scale(0.97);
}

.delete-modal-leave-to .modal {
  opacity: 0;

  transform:
    translateY(5px)
    scale(0.98);
}

/* ============================================================
   MOBILE
   ORIGINAL MAIN LAYOUT NOT MODIFIED
============================================================ */

@media (max-width: 430px) {

  .modal {
    width: calc(100% - 32px);

    box-sizing: border-box;
  }

  .chat-placeholder-inner {
    max-width: 280px;
  }

}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .chat-placeholder-inner,
  .delete-spinner {
    animation: none;
  }

  .chat-placeholder-image,
  .chat-placeholder-text,
  .cancel-btn,
  .delete-btn,
  .delete-modal-enter-active,
  .delete-modal-leave-active,
  .delete-modal-enter-active .modal,
  .delete-modal-leave-active .modal {
    transition: none;
  }

}


/* ============================================================
   EMPTY CHAT — MAIN CONTAINER
============================================================ */

.chat-placeholder {
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 0;

  position: relative;

  overflow: hidden;

  background: var(--dialogs-bg);
}

/* ============================================================
   EMPTY CHAT — CONTENT
============================================================ */

.chat-placeholder-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;

  width: 100%;
  max-width: 340px;

  text-align: center;

  animation: placeholderAppear 0.45s ease both;
}

/* ============================================================
   ILLUSTRATION
============================================================ */

.placeholder-illustration {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 240px;
  height: 210px;

  margin-bottom: 5px;
}

/* BACKGROUND GLOW */

.illustration-glow {
  position: absolute;

  width: 190px;
  height: 190px;

  border-radius: 50%;

  background: radial-gradient(
    circle,
    rgba(33, 115, 229, 0.085) 0%,
    rgba(33, 115, 229, 0.025) 48%,
    transparent 75%
  );

  pointer-events: none;
}

/* CIRCLE */

.illustration-ring {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 175px;
  height: 175px;

  border-radius: 50%;

  border: 1px solid rgba(33, 115, 229, 0.09);

  background: rgba(33, 115, 229, 0.025);
}

/* ============================================================
   CHAT CARD
============================================================ */

.illustration-chat {
  display: flex;
  flex-direction: column;

  gap: 10px;

  width: 132px;
  height: 112px;

  padding: 13px;

  border-radius: 19px;

  background: #ffffff;

  border: 1px solid #e7edf5;

  box-shadow:
    0 14px 35px rgba(31, 66, 112, 0.075),
    0 3px 10px rgba(31, 66, 112, 0.025);

  box-sizing: border-box;

  transform: rotate(-3deg);

  animation: illustrationFloat 5s ease-in-out infinite;
}

/* ============================================================
   CHAT BUBBLES
============================================================ */

.illustration-message {
  display: flex;
  flex-direction: column;

  gap: 5px;

  width: 76px;

  padding: 9px 10px;

  border-radius: 11px;

  box-sizing: border-box;
}

/* INCOMING */

.illustration-message.incoming {
  align-self: flex-start;

  background: #f0f3f7;

  border-top-left-radius: 4px;
}

/* OUTGOING */

.illustration-message.outgoing {
  align-self: flex-end;

  background: #2173e5;

  border-top-right-radius: 4px;
}

/* TEXT LINES */

.illustration-line {
  display: block;

  height: 4px;

  border-radius: 999px;
}

.illustration-line.long {
  width: 100%;
}

.illustration-line.short {
  width: 65%;
}

.illustration-message.incoming .illustration-line {
  background: #b9c5d5;
}

.illustration-message.outgoing .illustration-line {
  background: rgba(255, 255, 255, 0.82);
}

.illustration-message.outgoing .illustration-line.short {
  background: rgba(255, 255, 255, 0.5);
}

/* ============================================================
   TYPING INDICATOR
============================================================ */

.illustration-typing {
  display: flex;
  align-items: center;

  gap: 4px;

  align-self: flex-start;

  margin-left: 3px;
}

.illustration-typing span {
  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: #aab9cc;

  animation: illustrationTyping 1.4s ease-in-out infinite;
}

.illustration-typing span:nth-child(2) {
  animation-delay: 0.2s;
}

.illustration-typing span:nth-child(3) {
  animation-delay: 0.4s;
}

/* ============================================================
   FLOATING CHECK
============================================================ */

.illustration-check {
  position: absolute;

  top: 16px;
  right: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 36px;
  height: 36px;

  border-radius: 50%;

  border: 1px solid #e7edf5;

  background: #ffffff;

  color: #27b782;

  box-shadow:
    0 5px 16px rgba(29, 60, 99, 0.07);

  animation: floatingIcon 4s ease-in-out infinite;
}

.illustration-check svg {
  width: 19px;
  height: 19px;
}

/* ============================================================
   FLOATING PLUS
============================================================ */

.illustration-plus {
  position: absolute;

  bottom: 20px;
  left: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 34px;
  height: 34px;

  border-radius: 12px;

  background: #eaf3ff;

  color: #2173e5;

  border: 2px solid #ffffff;

  box-shadow:
    0 5px 16px rgba(33, 115, 229, 0.09);

  animation: floatingIcon 4.5s ease-in-out infinite reverse;
}

.illustration-plus svg {
  width: 18px;
  height: 18px;
}

/* ============================================================
   BADGE
============================================================ */

.placeholder-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  margin-top: 7px;
  padding: 7px 11px;

  border-radius: 999px;

  background: rgba(33, 115, 229, 0.075);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;

  letter-spacing: 0.8px;

  color: #2173e5;
}

.placeholder-badge-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: currentColor;
}

/* ============================================================
   TITLE
============================================================ */

.placeholder-title {
  margin: 18px 0 0;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 21px;
  line-height: 1.3;

  letter-spacing: -0.5px;

  color: var(--dialogs-text);
}

/* ============================================================
   DESCRIPTION
============================================================ */

.chat-placeholder-text {
  max-width: 300px;

  margin: 10px 0 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.7;

  letter-spacing: 0;

  color: var(--dialogs-secondary);

  text-align: center;
}

/* ============================================================
   HINT
============================================================ */

.placeholder-hint {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  margin-top: 25px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;

  color: var(--dialogs-muted);
}

.placeholder-hint svg {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  color: var(--dialogs-accent);
}

/* ============================================================
   DARK THEME — LOCAL CLASS
============================================================ */

.dialogs-container.dark-theme .illustration-glow {
  background: radial-gradient(
    circle,
    rgba(33, 115, 229, 0.12) 0%,
    rgba(33, 115, 229, 0.035) 48%,
    transparent 75%
  );
}

.dialogs-container.dark-theme .illustration-ring {
  background: rgba(33, 115, 229, 0.035);

  border-color: rgba(111, 168, 255, 0.11);
}

.dialogs-container.dark-theme .illustration-chat {
  background: #222222;

  border-color: rgba(255, 255, 255, 0.09);

  box-shadow:
    0 15px 38px rgba(0, 0, 0, 0.28);
}

.dialogs-container.dark-theme .illustration-message.incoming {
  background: #333333;
}

.dialogs-container.dark-theme
.illustration-message.incoming
.illustration-line {
  background: #777f8a;
}

.dialogs-container.dark-theme .illustration-typing span {
  background: #788394;
}

.dialogs-container.dark-theme .illustration-check {
  background: #252525;

  border-color: rgba(255, 255, 255, 0.1);

  box-shadow:
    0 6px 18px rgba(0, 0, 0, 0.22);
}

.dialogs-container.dark-theme .illustration-plus {
  background: #24364c;

  border-color: #161616;

  color: #83b6ff;
}

.dialogs-container.dark-theme .placeholder-badge {
  background: rgba(111, 168, 255, 0.12);

  color: #83b6ff;
}

/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes illustrationFloat {
  0%, 100% {
    transform: translateY(0) rotate(-3deg);
  }

  50% {
    transform: translateY(-6px) rotate(-2deg);
  }
}

@keyframes floatingIcon {
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-5px);
  }
}

@keyframes illustrationTyping {
  0%, 60%, 100% {
    opacity: 0.4;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@keyframes placeholderAppear {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {
  .chat-placeholder-inner,
  .illustration-chat,
  .illustration-check,
  .illustration-plus,
  .illustration-typing span {
    animation: none;
  }
}


</style>
